import { useCallback, useEffect, useRef, useState } from 'react';
import { transcribeDictation } from '../../services/RealtimeTokenService';

export type VoiceStatus = 'idle' | 'recording' | 'transcribing';

interface UseVoiceDictationArgs {
  onLive?: (committed: string, partial: string) => void;
  onDelta?: (delta: string) => void;
  onResult?: (fullText: string) => void;
  onError?: (message: string) => void;
}

function pickMime(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
  for (const t of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t)) return t;
  }
  return '';
}

/**
 * Dictare vocală pentru asistent — GPT-4o Transcribe.
 * Înregistrează tot clipul; transcrie o singură dată, la Stop, și pune tot textul în input.
 */
export function useVoiceDictation({ onLive, onDelta, onResult, onError }: UseVoiceDictationArgs) {
  const [status, setStatus] = useState<VoiceStatus>('idle');
  const cancelledRef = useRef(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number>(0);
  const mimeRef = useRef('audio/webm');

  const supported = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined';

  const stopTracks = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
  };

  const start = useCallback(async () => {
    if (!supported || status !== 'idle') return;
    cancelledRef.current = false;
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      streamRef.current = stream;
      const mime = pickMime();
      mimeRef.current = mime || 'audio/webm';
      const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recorderRef.current = recorder;
      startedAtRef.current = Date.now();

      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      recorder.onerror = () => onError?.('Eroare la înregistrarea microfonului.');

      recorder.start();
      setStatus('recording');
    } catch (e) {
      stopTracks();
      const name = (e as Error)?.name;
      onError?.(name === 'NotAllowedError' ? 'Acces la microfon refuzat. Permite microfonul în browser.' : 'Nu am putut porni dictarea vocală.');
    }
  }, [supported, status, onError]);

  const stop = useCallback(() => {
    const rec = recorderRef.current;
    if (!rec || rec.state === 'inactive') {
      if (status !== 'idle') setStatus('idle');
      return;
    }
    setStatus('transcribing');
    rec.onstop = () => {
      const durationSec = Math.max(0.4, (Date.now() - startedAtRef.current) / 1000);
      const parts = chunksRef.current;
      chunksRef.current = [];
      stopTracks();
      if (cancelledRef.current) {
        setStatus('idle');
        return;
      }
      if (!parts.length) {
        onError?.('Nu s-a înregistrat audio.');
        setStatus('idle');
        return;
      }
      const blob = new Blob(parts, { type: mimeRef.current });
      void transcribeDictation(blob, durationSec)
        .then((text) => {
          if (cancelledRef.current) return;
          onLive?.(text, '');
          onDelta?.(text);
          onResult?.(text);
        })
        .catch((e) => {
          if (!cancelledRef.current) onError?.((e as Error)?.message || 'Transcrierea a eșuat.');
        })
        .finally(() => setStatus('idle'));
    };
    rec.stop();
  }, [status, onLive, onDelta, onResult, onError]);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
    chunksRef.current = [];
    stopTracks();
    setStatus('idle');
  }, []);

  const toggle = useCallback(() => {
    if (status === 'recording') stop();
    else if (status === 'idle') void start();
  }, [status, start, stop]);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      try {
        recorderRef.current?.stop();
      } catch {
        /* noop */
      }
      stopTracks();
    };
  }, []);

  return {
    status,
    supported,
    isRecording: status === 'recording',
    isTranscribing: status === 'transcribing',
    start,
    stop,
    cancel,
    toggle,
  };
}
